import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/modern/components/ui/dialog-shadcn';
import { Button } from '@/modern/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/modern/components/ui/radio-group';
import { Label } from '@/modern/components/ui/label';
import { Input } from '@/modern/components/ui/input';
import { cn } from '@/modern/utils';
import { capitalizeText } from '@/other/utils/utils';

interface DuplicateEntityResolutionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardName: string;
  originalEntityName: string;
  entityType: string;
  onResolve: (action: 'replace' | 'duplicate', entityName?: string) => Promise<void>;
  onCancel: () => void;
  copyMetadata?: boolean;
  archiveOriginal?: boolean;
}

export const DuplicateEntityResolutionDialog: React.FC<DuplicateEntityResolutionDialogProps> = ({
  open,
  onOpenChange,
  boardName,
  originalEntityName,
  entityType,
  onResolve,
  onCancel,
  copyMetadata = false,
  archiveOriginal = false,
}) => {
  const [selectedAction, setSelectedAction] = useState<'duplicate' | 'replace'>('duplicate');
  const [customEntityName, setCustomEntityName] = useState<string>(`Copy of ${originalEntityName}`);
  const [isLoading, setIsLoading] = useState(false);

  const entityTypeLabel = capitalizeText(entityType);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedAction('duplicate');
      setCustomEntityName(`Copy of ${originalEntityName}`);
      setIsLoading(false);
    }
  }, [open, originalEntityName]);

  const handleContinue = async () => {
    if (selectedAction === 'duplicate' && !customEntityName.trim()) {
      return; // Don't proceed if entity name is empty
    }

    setIsLoading(true);
    try {
      await onResolve(
        selectedAction,
        selectedAction === 'duplicate' ? customEntityName.trim() : undefined
      );
    } catch (error) {
      // Error handling is done in the parent component
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const isContinueDisabled = isLoading || (selectedAction === 'duplicate' && !customEntityName.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="sm:max-w-[600px]" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-xl">
            Duplicate {entityTypeLabel} Detected
          </DialogTitle>
          <DialogDescription>
            A {entityType} with the name <strong>"{originalEntityName}"</strong> already exists on <strong>{boardName}</strong>. How would you like to proceed?
          </DialogDescription>
        </DialogHeader>

        <div>
          <RadioGroup
            value={selectedAction}
            onValueChange={(value) => setSelectedAction(value as 'duplicate' | 'replace')}
            className="space-y-3"
          >
            {/* Duplicate Option */}
            <div
              className={cn(
                'rounded-lg border p-4 cursor-pointer transition-all',
                selectedAction === 'duplicate'
                  ? 'border-primary bg-primary/10 shadow-sm'
                  : 'border-input hover:border-primary/50 hover:bg-accent/50'
              )}
              onClick={() => setSelectedAction('duplicate')}
            >
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="duplicate" id="duplicate" className="mt-0.5" />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="duplicate" className="text-base font-semibold cursor-pointer">
                    Duplicate
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Create a copy on <strong>{boardName}</strong>
                  </p>
                  {selectedAction === 'duplicate' && (
                    <div className="pt-2" onClick={(e) => e.stopPropagation()}>
                      <Input
                        type="text"
                        value={customEntityName}
                        onChange={(e) => setCustomEntityName(e.target.value)}
                        placeholder={`Enter ${entityType} name`}
                        className="w-full bg-background focus-visible:ring-0 focus-visible:ring-offset-0"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Replace Option */}
            <div
              className={cn(
                'rounded-lg border p-4 cursor-pointer transition-all',
                selectedAction === 'replace'
                  ? 'border-primary bg-primary/10 shadow-sm'
                  : 'border-input hover:border-primary/50 hover:bg-accent/50'
              )}
              onClick={() => setSelectedAction('replace')}
            >
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="replace" id="replace" className="mt-0.5" />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="replace" className="text-base font-semibold cursor-pointer">
                    Replace
                  </Label>
                  <div className="flex items-center gap-1.5 text-sm text-destructive font-medium">
                    <i className="fa-solid fa-triangle-exclamation" />
                    This cannot be undone
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Replace existing {entityType} on <strong>{boardName}</strong>
                  </p>
                </div>
              </div>
            </div>
          </RadioGroup>

          {/* Options Display (Read-only) */}
          {(copyMetadata || archiveOriginal) && (
            <div className="space-y-3 pt-4 border-t mt-4">
              {copyMetadata && (
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5">
                    <i className="fa-solid fa-check text-primary text-xs" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-medium leading-none text-muted-foreground">
                      Copy category, tags, status, team and phase selections
                    </Label>
                  </div>
                </div>
              )}

              {archiveOriginal && (
                <div className="flex items-start space-x-3">
                  <div className="flex items-center h-5">
                    <i className="fa-solid fa-check text-primary text-xs" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-medium leading-none text-muted-foreground">
                      On the sending board, archive after copy.
                    </Label>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            disabled={isContinueDisabled}
            className="min-w-[120px]"
          >
            {isLoading ? (
              <>
                <i className="fa-light fa-spinner fa-spin mr-2" />
                Processing...
              </>
            ) : (
              <>Continue</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
